import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Check, X, Ship, Sun, Sunset, Users, UtensilsCrossed,
  AlertTriangle, Search, ChevronDown, ChevronUp
} from "lucide-react";

const API = window.location.origin;

const CATEGORY_ICONS = {
  "utensils": UtensilsCrossed,
  "sunset": Sunset,
  "users": Users,
  "sun": Sun,
};

export default function CategorySettings() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);

  const token = localStorage.getItem("auth_token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [catRes, prodRes, mapRes] = await Promise.all([
        axios.get(`${API}/api/categories`, { headers }),
        axios.get(`${API}/api/products/unique`, { headers }),
        axios.get(`${API}/api/category-mappings`, { headers }),
      ]);
      setCategories(catRes.data.categories || []);
      setProducts(prodRes.data.products || []);
      setMappings(mapRes.data.mappings || []);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const productCategoryMap = {};
  for (const m of mappings) {
    productCategoryMap[m.product_name] = m.category_id;
  }

  const assignProduct = async (productName, categoryId) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/category-mappings`, {
        category_id: categoryId,
        product_name: productName,
      }, { headers });
      await fetchAll();
    } catch (err) {
      console.error("Assign error:", err);
    }
    setSaving(false);
  };

  const removeMapping = async (productName) => {
    setSaving(true);
    try {
      const mapping = mappings.find(m => m.product_name === productName);
      if (mapping) {
        await axios.delete(`${API}/api/category-mappings/${mapping.id}`, { headers });
        await fetchAll();
      }
    } catch (err) {
      console.error("Remove error:", err);
    }
    setSaving(false);
  };

  const unmappedProducts = products.filter(p => !productCategoryMap[p.product_name]);
  const filteredUnmapped = unmappedProducts.filter(p =>
    p.product_name.toLowerCase().includes(search.toLowerCase())
  );

  const getMappedProducts = (catId) => {
    return products.filter(p => productCategoryMap[p.product_name] === catId);
  };

  return (
    <div className="p-4 lg:p-6 space-y-6 max-w-5xl" data-testid="category-settings">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/sheet"
          className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-slate-400"
          data-testid="back-to-sheet"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight">Urun Kategori Esleme</h1>
          <p className="text-xs text-slate-500 mt-0.5">Bokun urunlerini kategorilere atayarak operasyon sayfasinda otomatik gruplama yapin</p>
        </div>
      </div>

      {/* Categories with mapped products */}
      <div className="space-y-3">
        {categories.map((cat) => {
          const Icon = CATEGORY_ICONS[cat.icon] || Ship;
          const mapped = getMappedProducts(cat.id);
          const isExpanded = expandedCat === cat.id;
          return (
            <div key={cat.id} className="rounded-lg border border-white/[0.06] bg-[#0D1117] overflow-hidden" data-testid={`category-section-${cat.id}`}>
              <button
                onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: cat.color + "20" }}>
                    <Icon className="w-4 h-4" style={{ color: cat.color }} />
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-semibold text-white">{cat.name}</div>
                    <div className="text-[11px] text-slate-500">{mapped.length} urun eslenmis</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono px-2 py-0.5 rounded-md" style={{ backgroundColor: cat.color + "15", color: cat.color }}>
                    {mapped.reduce((s, p) => s + p.count, 0)} rez.
                  </span>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-white/[0.06] px-4 py-3">
                  {mapped.length === 0 ? (
                    <div className="text-xs text-slate-600 py-2">Henuz urun eslenmemis</div>
                  ) : (
                    <div className="space-y-1">
                      {mapped.map((p) => (
                        <div key={p.product_name} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-white/[0.03] group">
                          <div className="flex items-center gap-2">
                            <Check className="w-3.5 h-3.5" style={{ color: cat.color }} />
                            <span className="text-sm text-slate-300">{p.product_name}</span>
                            <span className="text-[10px] text-slate-600 font-mono">({p.count})</span>
                          </div>
                          <button
                            onClick={() => removeMapping(p.product_name)}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-red-400 hover:bg-red-500/10 transition-all"
                            title="Eslemeyi kaldir"
                            data-testid={`remove-mapping-${p.product_name}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Unmapped Products */}
      <div className="rounded-lg border border-amber-500/20 bg-[#0D1117] overflow-hidden" data-testid="unmapped-section">
        <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-semibold text-white">Eslenmemis Urunler</span>
            <span className="text-xs text-amber-400/70">({unmappedProducts.length})</span>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Urun ara..."
              className="bg-white/[0.04] border border-white/[0.08] rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-slate-600 outline-none focus:border-blue-500/30 w-48"
              data-testid="product-search"
            />
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {filteredUnmapped.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-slate-600">
              {unmappedProducts.length === 0 ? "Tum urunler eslenmis" : "Sonuc bulunamadi"}
            </div>
          ) : (
            <div className="divide-y divide-white/[0.03]">
              {filteredUnmapped.map((p) => (
                <div key={p.product_name} className="flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02]" data-testid={`unmapped-product-${p.product_name}`}>
                  <div>
                    <div className="text-sm text-slate-300">{p.product_name}</div>
                    <div className="text-[10px] text-slate-600 font-mono">{p.count} rezervasyon</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {categories.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.icon] || Ship;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => assignProduct(p.product_name, cat.id)}
                          disabled={saving}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-all hover:scale-105"
                          style={{ backgroundColor: cat.color + "15", color: cat.color, border: `1px solid ${cat.color}30` }}
                          title={`${cat.name} kategorisine ata`}
                          data-testid={`assign-${p.product_name}-to-${cat.id}`}
                        >
                          <Icon className="w-3 h-3" />
                          <span className="hidden sm:inline">{cat.name.split(" ")[0]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-500/20 border border-blue-500/30 rounded-lg px-4 py-2 text-xs text-blue-400 flex items-center gap-2">
          <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          Kaydediliyor...
        </div>
      )}
    </div>
  );
}
